import { z } from "zod";
import { canonicalSha256 } from "./canonicalize.js";
import { protocolIdSchema, protocolTimestampSchema, protocolVersionSchema, sha256DigestSchema, UNIVERSAL_VERIFICATION_PROTOCOL, } from "./identifiers.js";
export const intentSourceKindSchema = z.enum([
    "organization_policy",
    "repository_policy",
    "requester_work_object",
    "trusted_workflow_template",
    "observed_occurrence",
    "worker_proposal",
]);
export const intentPermissionTierSchema = z.enum([
    "presence_metadata",
    "aggregate_event_classes",
    "redacted_structure",
    "content_bearing",
]);
export const sourceIntentOutcomeInputSchema = z
    .object({
    id: protocolIdSchema,
    statement: z.string().trim().min(1),
    span: z.string().trim().min(1),
    contradicts: z.array(protocolIdSchema).default([]),
})
    .strict();
export const codingTaskRequirementSchema = z
    .object({
    outcomeId: protocolIdSchema,
    statement: z.string().trim().min(1),
    sourceSpan: z.string().trim().min(1),
    observation: z
        .object({
        kind: z.enum(["command_exit", "artifact_present"]),
        ref: protocolIdSchema,
    })
        .strict(),
})
    .strict();
export class CodingTaskIntentRefusal extends Error {
    reason;
    constructor(reason, detail) {
        super(`${reason}: ${detail}`);
        this.name = "CodingTaskIntentRefusal";
        this.reason = reason;
    }
}
function normalizedTaskText(value) {
    return value.replace(/\s+/g, " ").trim();
}
function normalizedRequirementClaim(value) {
    return normalizedTaskText(value)
        .replace(/[.!?]+$/u, "")
        .toLocaleLowerCase();
}
function requirementIsOnlyVague(statement) {
    return /^(?:make|ensure|keep)?\s*(?:it|this|the\s+(?:product|system|code))?\s*(?:better|good|clean|robust|production[- ]ready|working|works)(?:\s+well)?[.!]?$/iu.test(normalizedTaskText(statement));
}
/**
 * Refuses coding-task intent that cannot become a task-outcome obligation.
 * Artifact existence is useful effect-closure evidence, but it cannot stand in
 * for observing the behavior the requester asked for.
 */
export function assertObservableCodingTaskIntent(input) {
    const taskText = normalizedTaskText(input.taskText);
    const unresolved = (input.unresolvedDecisions ?? []).map(normalizedTaskText).filter(Boolean);
    if (unresolved.length > 0) {
        throw new CodingTaskIntentRefusal("coding-task-unresolved-decisions", unresolved.join("; "));
    }
    const requirements = input.requirements.map((requirement) => codingTaskRequirementSchema.parse(requirement));
    const outcomeIds = requirements.map((requirement) => requirement.outcomeId);
    if (new Set(outcomeIds).size !== outcomeIds.length) {
        throw new CodingTaskIntentRefusal("coding-task-outcome-duplicate", "each requested outcome must have one canonical requirement");
    }
    const observationRefs = requirements.map((requirement) => requirement.observation.ref);
    if (new Set(observationRefs).size !== observationRefs.length) {
        throw new CodingTaskIntentRefusal("coding-task-observation-duplicate", "each task outcome requires its own observation binding");
    }
    for (const requirement of requirements) {
        const sourceSpan = normalizedTaskText(requirement.sourceSpan);
        if (!taskText.includes(sourceSpan)) {
            throw new CodingTaskIntentRefusal("coding-task-requirement-ungrounded", `${requirement.outcomeId} does not cite text present in the task`);
        }
        if (normalizedRequirementClaim(requirement.statement) !==
            normalizedRequirementClaim(requirement.sourceSpan)) {
            throw new CodingTaskIntentRefusal("coding-task-requirement-ungrounded", `${requirement.outcomeId} statement is not the quoted task span`);
        }
        if (requirementIsOnlyVague(requirement.statement)) {
            throw new CodingTaskIntentRefusal("coding-task-requirement-ambiguous", `${requirement.outcomeId} does not state concrete requested behavior`);
        }
        if (requirement.observation.kind !== "command_exit") {
            throw new CodingTaskIntentRefusal("coding-task-requirement-unobservable", `${requirement.outcomeId} is backed only by generic artifact existence`);
        }
    }
    if (requirements.length === 0) {
        throw new CodingTaskIntentRefusal("coding-task-requirement-unobservable", "at least one command-observed requested outcome is required");
    }
    return requirements;
}
export const sourceIntentInputSchema = z
    .object({
    id: protocolIdSchema,
    kind: intentSourceKindSchema,
    digest: sha256DigestSchema,
    permissionTier: intentPermissionTierSchema,
    collectedAt: protocolTimestampSchema,
    revokedAt: protocolTimestampSchema.nullable(),
    retainedAfterRevocation: z.boolean(),
    outcomes: z.array(sourceIntentOutcomeInputSchema),
})
    .strict();
export function createObservedOccurrenceIntentSource(input) {
    const permissionTier = intentPermissionTierSchema.parse(input.permissionTier);
    const outcomes = permissionTier === "content_bearing" ? [...(input.outcomes ?? [])] : [];
    return sourceIntentInputSchema.parse({
        id: input.id,
        kind: "observed_occurrence",
        digest: input.digest,
        permissionTier,
        collectedAt: input.collectedAt,
        revokedAt: input.revokedAt ?? null,
        retainedAfterRevocation: input.retainedAfterRevocation ?? false,
        outcomes,
    });
}
export const inferredIntentOutcomeSchema = z
    .object({
    id: protocolIdSchema,
    statement: z.string().trim().min(1),
    sourceRefs: z
        .array(z
        .object({
        sourceId: protocolIdSchema,
        sourceKind: intentSourceKindSchema,
        digest: sha256DigestSchema,
        span: z.string().trim().min(1),
    })
        .strict())
        .min(1),
    precedence: z.number().int().nonnegative(),
})
    .strict();
export const inferredIntentConflictSchema = z
    .object({
    outcomeId: protocolIdSchema,
    sourceIds: z.array(protocolIdSchema).min(2),
    reason: z.string().trim().min(1),
})
    .strict();
export const inferredIntentSchema = z
    .object({
    protocolVersion: protocolVersionSchema,
    id: protocolIdSchema,
    status: z.enum(["inferred", "needs_confirmation", "abstain"]),
    outcomes: z.array(inferredIntentOutcomeSchema),
    alternatives: z.array(z.string().trim().min(1)),
    contradictions: z.array(inferredIntentConflictSchema),
    limitedByPermissions: z.array(protocolIdSchema),
    stoppedSources: z.array(protocolIdSchema),
    retainedArtifactDigests: z.array(sha256DigestSchema),
    sourceSetDigest: sha256DigestSchema,
    intentDigest: sha256DigestSchema,
})
    .strict()
    .superRefine((value, context) => {
    if (value.status === "inferred" && value.outcomes.length === 0) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["outcomes"],
            message: "inferred intent requires at least one source-backed outcome",
        });
    }
    if (value.status !== "inferred" && value.outcomes.length > 0) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["outcomes"],
            message: "ambiguous or confirmation-needed intent cannot invent outcomes",
        });
    }
});
const PRECEDENCE = {
    organization_policy: 0,
    repository_policy: 1,
    requester_work_object: 2,
    trusted_workflow_template: 3,
    observed_occurrence: 4,
    worker_proposal: 5,
};
function digestIntent(input, sha256) {
    return canonicalSha256(input, sha256);
}
function isRevoked(source, now) {
    return source.revokedAt !== null && source.revokedAt <= now;
}
function hasContentPermission(source) {
    return source.permissionTier === "content_bearing";
}
function looksAmbiguous(sources) {
    const text = sources
        .flatMap((source) => source.outcomes.map((outcome) => outcome.statement))
        .join(" ")
        .toLowerCase();
    return (/\bproduction ready\b/.test(text) &&
        !/\b(retry|resume|chunk|availability|performance|durability|latency|uptime|backup|restore)\b/.test(text));
}
export function inferSourceBackedIntent(input) {
    const sources = input.sources
        .map((source) => sourceIntentInputSchema.parse(source))
        .sort((left, right) => PRECEDENCE[left.kind] - PRECEDENCE[right.kind] || left.id.localeCompare(right.id));
    const stoppedSources = sources
        .filter((source) => isRevoked(source, input.now))
        .map((source) => source.id);
    const retainedArtifactDigests = sources
        .filter((source) => isRevoked(source, input.now) && source.retainedAfterRevocation)
        .map((source) => source.digest)
        .sort();
    const active = sources.filter((source) => !isRevoked(source, input.now));
    const limitedByPermissions = active
        .filter((source) => !hasContentPermission(source))
        .map((source) => source.id)
        .sort();
    const contentSources = active.filter(hasContentPermission);
    const sourceSetDigest = canonicalSha256(sources.map((source) => ({
        id: source.id,
        kind: source.kind,
        digest: source.digest,
        permissionTier: source.permissionTier,
        revokedAt: source.revokedAt,
        retainedAfterRevocation: source.retainedAfterRevocation,
    })), input.sha256);
    if (contentSources.length === 0 || looksAmbiguous(contentSources)) {
        const withoutDigest = {
            protocolVersion: UNIVERSAL_VERIFICATION_PROTOCOL,
            id: input.id,
            status: contentSources.length === 0 ? "abstain" : "needs_confirmation",
            outcomes: [],
            alternatives: contentSources.length === 0
                ? ["Grant content-bearing source access or provide an explicit work object."]
                : [
                    "Confirm concrete availability targets.",
                    "Confirm concrete performance targets.",
                    "Confirm concrete durability targets.",
                ],
            contradictions: [],
            limitedByPermissions,
            stoppedSources,
            retainedArtifactDigests,
            sourceSetDigest,
        };
        return inferredIntentSchema.parse({
            ...withoutDigest,
            intentDigest: digestIntent(withoutDigest, input.sha256),
        });
    }
    const selected = new Map();
    const contradictions = [];
    for (const source of contentSources) {
        for (const outcome of source.outcomes) {
            const parsed = sourceIntentOutcomeInputSchema.parse(outcome);
            for (const contradicted of parsed.contradicts) {
                const existing = selected.get(contradicted);
                if (existing) {
                    contradictions.push(inferredIntentConflictSchema.parse({
                        outcomeId: contradicted,
                        sourceIds: [existing.sourceRefs[0].sourceId, source.id].sort(),
                        reason: `${source.kind} contradicts previously selected outcome ${contradicted}`,
                    }));
                }
            }
            const candidate = inferredIntentOutcomeSchema.parse({
                id: parsed.id,
                statement: parsed.statement,
                sourceRefs: [
                    {
                        sourceId: source.id,
                        sourceKind: source.kind,
                        digest: source.digest,
                        span: parsed.span,
                    },
                ],
                precedence: PRECEDENCE[source.kind],
            });
            const existing = selected.get(candidate.id);
            if (!existing || candidate.precedence < existing.precedence) {
                selected.set(candidate.id, candidate);
            }
            else if (existing.statement !== candidate.statement) {
                contradictions.push(inferredIntentConflictSchema.parse({
                    outcomeId: candidate.id,
                    sourceIds: [existing.sourceRefs[0].sourceId, source.id].sort(),
                    reason: "lower-precedence source conflicts with canonical outcome",
                }));
            }
        }
    }
    const outcomes = [...selected.values()].sort((left, right) => left.id.localeCompare(right.id));
    const withoutDigest = {
        protocolVersion: UNIVERSAL_VERIFICATION_PROTOCOL,
        id: input.id,
        status: outcomes.length > 0 ? "inferred" : "abstain",
        outcomes,
        alternatives: [],
        contradictions: contradictions.sort((left, right) => left.outcomeId.localeCompare(right.outcomeId)),
        limitedByPermissions,
        stoppedSources,
        retainedArtifactDigests,
        sourceSetDigest,
    };
    return inferredIntentSchema.parse({
        ...withoutDigest,
        intentDigest: digestIntent(withoutDigest, input.sha256),
    });
}
